import { useCallback, useEffect, useRef, useState } from "react";
import type { InvoiceSummary } from "@/shared/ui/InvoiceInfo";
import {
  apiAssetUrl,
  apiDelete,
  apiGet,
  apiPatch,
  apiPost,
  apiPut,
  filenameFromContentDisposition,
  prettifyApiError,
} from "@/shared/api";
import { downloadFile } from "@/shared/download";
import type { MailOverride, MailPreviewRead } from "@/features/mail/api";
import { skusContainAllService } from "./allserviceApi";
import { IVA_RATE } from "./economicSummary";
import { type ApiTrackingEvent, mapTrackingEvent } from "./mapTrackingEvent";
import type {
  FornitoreKind,
  Marketplace,
  Order,
  ReplacementAddress,
  OrderShipment,
  OrderState,
  PaymentState,
} from "./types";
import type { RitiriOggi } from "./data/mockRitiri";

type ApiOrderItem = {
  id: number;
  sku: string;
  title: string;
  quantity: number;
  unit_price: string | number;
  image_url?: string;
};

type ApiOrder = {
  id: number;
  poleepo_id: number;
  reference: string;
  channel: string;
  status: string;
  state: OrderState;
  payment_state: PaymentState;
  created_at_poleepo?: string | null;
  updated_at_poleepo?: string | null;
  total_amount: string | number;
  customer_name: string;
  customer_email: string;
  customer_phone: string;
  shipping_address: string;
  shipping_postcode: string;
  shipping_city: string;
  shipping_province: string;
  shipping_country?: string;
  shipping_address_edited_at?: string | null;
  carrier: string;
  supplier_order_ref: string;
  supplier_cost: string | number | null;
  supplier_kind?: FornitoreKind;
  // Timestamp ISO dell'invio a Cosma (sent-log). "" / assente = non inviato o
  // ordine non-Cosma. La UI lo usa per "Inviato a Cosma" al posto del
  // fuorviante "Da sincronizzare" (Cosma e' FTP one-way: nessun N.Ord.Forn torna).
  cosma_sent_at?: string;
  paid: boolean;
  currency?: string;
  ldv_code?: string;
  // Computed field backend OrderRead: vero quando arrivata email
  // "merce pronta" da Itamoby/Ityhome e ordine non ancora spedito.
  pickup_ready?: boolean;
  pickup_ready_at?: string | null;
  // Computed field backend: vero quando scanner Sent IMAP ha rilevato
  // la mail LDV gia' inviata al fornitore.
  pickup_mail_sent?: boolean;
  pickup_mail_sent_at?: string | null;
  // Computed field backend OrderRead: email "Merce pronta" arrivata OGGI
  // (TZ Rome) per ordine Itamoby/Ityhome, anche se gia' spedito.
  merce_pronta_today?: boolean;
  // Computed field backend OrderRead: allarme "LDV MAI INVIATA" — ordine
  // Ita/Ity spedito senza nessuna evidenza di invio mail (fix 2026-07-16).
  pickup_mail_missing?: boolean;
  packages_count: number;
  // Audit 2026-05-14: colli/dimensioni dal listino fornitore Itamoby/Ityhome
  // (catalog.supplier_catalog_entries). Backend popola questo array quando
  // il primo SKU dell'ordine matcha un'entry nel listino. Frontend lo usa
  // per pre-popolare ``colli`` quando ``packages_count=0`` e bloccare il
  // bottone "Genera LDV" se nessun valore e' presente.
  suggested_packages?: Array<{ kg?: number; l?: number; a?: number; p?: number }> | null;
  // Numero colli "naturale" del listino (Σ quantita' × colli per SKU): il
  // frontend ci pre-popola "N° Colli" invece del fisso 1. Bug 2026-06-08.
  suggested_packages_count?: number | null;
  // Doppione Poleepo: la stessa reference ha piu' di un ordine (la lista ne
  // mostra uno solo, deduplicato). Bug 2026-06-08.
  is_duplicate?: boolean;
  // Dati spedizione salvati "al volo" da Davide (OrderRead.package_dimensions
  // / use_pallet). Prevalgono su suggested_packages nel pre-fill dell'editor.
  use_pallet?: boolean;
  // Flag bancale auto-rilevato (OrderRead.usa_bancale, computed da
  // raw.usa_bancale). Audit 2026-05-22: la checkbox "Bancale" dell'editor
  // si pre-spunta se questo o use_pallet sono true.
  usa_bancale?: boolean;
  package_dimensions?: Array<{ kg: number; l: number; a: number; p: number }>;
  items: ApiOrderItem[];
  raw?: Record<string, unknown> | null;
  // Computed field backend OrderRead: vero quando >=1 SKU dell'ordine e'
  // nel foglio "Magazzino" (merce gia' in casa BSCom).
  in_warehouse?: boolean;
  // Fase A multi-fornitore (2026-07-06): sku → kind per singolo item +
  // flag "misto" (fornitori riconosciuti diversi tra gli item).
  item_supplier_kinds?: Record<string, FornitoreKind> | null;
  fornitori_merce?: Record<string, string> | null;
  is_mixed?: boolean;
  allservice_eligible?: boolean;
  // Computed field backend OrderRead: vero quando l'ordine va a Web Furniture
  // (SKU nel catalogo webfurniture, non-AS, fornitore unknown). Gate del
  // bottone "Invia ordine WF".
  wf_eligible?: boolean;
  // A quale fornitore l'ordine si manda via mail (id: webfurniture, aurora),
  // dal PIM: gate del tasto per i fornitori senza catalogo OM.
  fornitore_mail?: string | null;
  // Su quale portale fornitore OM sa ORDINARE questo ordine (backend
  // ``_portale_fornitore``): gate dei bottoni "Ordina a Ityhome/Itamoby".
  // Diverso da ``supplier_kind`` di proposito — vedi il commento nel router.
  portale_fornitore?: string | null;
  // Computed field backend: almeno una mail reale in outbox per la reference.
  // Gate del bottone "Mail" sulla card (Davide 2026-07-02).
  has_outbox_mail?: boolean;
  // Colonna locale Order.tracking_code (scritta da Genera LDV): nel DB reale
  // e' QUI, non in raw.tracking_code (124/200 ordini live). Alimenta la pill
  // tracking del design B.
  tracking_code?: string | null;
  // Ultimo evento corriere cacheato dal job orario tracking_events_pull
  // (2026-07-03): pill con lo stato reale del corriere, verde se consegnato.
  last_tracking_event?: ApiTrackingEvent | null;
  // Deadline spedizione dal marketplace (job ship_deadline_pull, R3).
  ship_deadline?: string | null;
  // Computed field backend OrderRead (item 13/20): fornitore da pagare +
  // stato invio ordine al fornitore (to_send/sent, null = senza flusso).
  supplier_unpaid?: boolean;
  supplier_send_state?: "to_send" | "sent" | null;
  // Computed field backend OrderRead (D1): cliente non ancora pagato (persiste
  // sugli spediti) + esiste un reso/sostituzione (rimborso in corso o chiuso).
  customer_unpaid?: boolean;
  has_return?: boolean;
  // Rimborso: esiste una proposta di nota di credito per l'ordine. L'importo è
  // `string` perché il backend serializza i Numeric come stringa (mai float:
  // su un valore fiscale l'arrotondamento binario non è accettabile).
  has_refund?: boolean;
  refund_amount?: string | number | null;
  address_issues?: string[] | null;
  // Shopify: righe a cui sono state tolte/rimborsate unita' (OrderRead.righe_ridotte).
  righe_ridotte?: { sku: string; ordinate: number; rimaste: number }[] | null;
  invoice?: InvoiceSummary | null;
  // ── Terzo indirizzo: la sostituzione da spedire (migr 0088) ──
  replacement_address?: ReplacementAddress | null;
  replacement_note?: string | null;
  replacement_set_at?: string | null;
  replacement_pending?: boolean;
  replacement_tracking?: string[];
};

type ApiOrderList = {
  items: ApiOrder[];
  total: number;
};

/**
 * Esito della comunicazione al marketplace d'origine, presente solo sugli
 * ordini NATIVI (quelli che su Poleepo non esistono): il backend, dopo aver
 * emesso l'etichetta, prova a chiudere l'ordine sul canale e a passargli il
 * tracking. È best-effort per scelta — se il canale non risponde il pacco
 * parte lo stesso — quindi l'esito DEVE arrivare all'operatore, altrimenti un
 * cliente resta senza tracking e nessuno lo sa.
 */
export type ConfermaCanale = {
  confermato: boolean;
  /**
   * `true` quando non c'era nulla da fare: l'ordine era già confermato spedito,
   * oppure il tracking era già stato scritto sul canale. NON è un guasto, e
   * mostrarlo in rosso è peggio che tacerlo — la ristampa di un'etichetta è il
   * clic più frequente, e un allarme che si accende quando va tutto bene
   * smette di essere un allarme.
   */
  gia_fatto?: boolean;
  /**
   * Amazon: il feed di conferma è stato preso in carico, ma l'ordine non
   * risulta ancora spedito — l'esito vero arriva col rapporto di
   * elaborazione, che legge il job `amazon_feeds_pull`. Distinto da
   * `confermato` apposta: chiamarlo successo sarebbe scrivere che è andata
   * bene prima di saperlo.
   */
  inviato?: boolean;
  feed_id?: string;
  motivo?: string;
  tracking_code?: string;
  carrier?: string;
};

export type GenerateLabelResult = {
  order_id: number;
  reference: string;
  file_path: string;
  file_name: string;
  tracking_code: string;
  carrier: string;
  label_url: string;
  shipping_id: number | null;
  cache_hit: boolean;
  conferma_canale?: ConfermaCanale | null;
};

/**
 * Risposta del backend quando l'ordine è destinato a SDA ma i colli effettivi
 * superano il limite SDA (15) e non c'è ancora consenso allo switch: la
 * spedizione NON viene creata e si propone Poste Delivery Business
 * (spec 2026-06-24). Discriminata da ``needs_carrier_switch: true``.
 */
export type CarrierSwitchRequired = {
  needs_carrier_switch: true;
  reference: string;
  /** Numero colli EFFETTIVI (post bancale/ricompattazione) > 15. */
  packages_count: number;
  /** Corriere attuale che non può gestire i colli (sempre "SDA"). */
  carrier: string;
  /** Corriere proposto in alternativa (sempre "POSTE_DELIVERY"). */
  proposed_carrier: string;
};

export interface SupplierPaid {
  order_id: number;
  reference: string;
  paid: boolean;
  paid_at: string | null;
}

/** Segna (o disfa) il pagamento al fornitore.
 *
 *  Backend: PATCH /api/orders/{ref}/supplier-paid. È il campo `paid`, quello
 *  che spegne il badge «fornitore da pagare»: resta scritto anche dopo il
 *  sync Poleepo (`LOCAL_ONLY_ORDER_FIELDS`). */
export function setSupplierPaid(orderRef: string, paid: boolean): Promise<SupplierPaid> {
  return apiPatch<SupplierPaid>(`/api/orders/${encodeURIComponent(orderRef)}/supplier-paid`, {
    paid,
  });
}

/** Esito di ``generateLabel``: LDV creata oppure richiesta di conferma switch. */
export type GenerateLabelResponse = GenerateLabelResult | CarrierSwitchRequired;

/** Type guard: la risposta è una richiesta di switch corriere (SDA>15)?
 *  Il caso di successo aggiunge ``needs_carrier_switch:false`` lato backend,
 *  quindi discriminiamo sul valore ``=== true``. Restringe ``result`` a
 *  ``GenerateLabelResult`` nel ramo negativo. */
export function isCarrierSwitchRequired(
  result: GenerateLabelResponse,
): result is CarrierSwitchRequired {
  return (result as CarrierSwitchRequired).needs_carrier_switch === true;
}

export type GenerateLabelOptions = {
  carrier?: string;
  label_format?: string;
  // N° Colli richiesto dalla card (WYSIWYG): il backend ricompatta i colli del
  // catalogo in questo numero se diverso dal naturale (bug 2026-06-08).
  packages_count?: number;
  // Forza rigenerazione anche se la LDV e' gia' in cache (purge+ricrea su
  // ordini non spediti). Inviato quando l'utente cambia i dati spedizione.
  force?: boolean;
  // "Rifai LDV" su ordine GIÀ SPEDITO: consenso esplicito (dialog di conferma)
  // ad annullare la spedizione Poleepo esistente e ricrearla. Senza, la guardia
  // backend lascia intatti gli ordini spediti.
  reship_when_shipped?: boolean;
};

/** Genera (o riusa cached) la LDV per un ordine NON-AllService.
 *  Backend: POST /api/orders/{ref}/generate-label → Poleepo /shippings
 *  con generate_label=True → download PDF/ZPL su disco → ritorna il
 *  path locale ``data_dir/labels/{ref}.pdf`` aprible via Electron
 *  ``omBridge.openPath``.
 *
 *  SDA>15 colli: se ``options.carrier`` non forza Poste, il backend può
 *  rispondere ``CarrierSwitchRequired`` (nessuna spedizione creata). Per
 *  confermare lo switch si ri-chiama con ``carrier:"POSTE_DELIVERY"``. Usa
 *  ``isCarrierSwitchRequired`` per discriminare l'esito. */
export function generateLabel(
  orderRef: string,
  options: GenerateLabelOptions = {},
): Promise<GenerateLabelResponse> {
  return apiPost<GenerateLabelResponse>(
    `/api/orders/${encodeURIComponent(orderRef)}/generate-label`,
    {
      carrier: options.carrier,
      label_format: options.label_format,
      packages_count: options.packages_count,
      force: options.force,
      reship_when_shipped: options.reship_when_shipped,
    },
  );
}

/** Esito preflight per-ordine (read-only): colli effettivi + corriere +
 *  ``needs_switch`` (SDA con >15 colli). Nessuna spedizione creata. */
export type PreflightItem = {
  reference: string;
  packages_count: number;
  carrier: string;
  needs_switch: boolean;
};

type PreflightResponse = { items: PreflightItem[] };

/** Preflight bulk LDV: per ogni ref calcola colli effettivi + carrier +
 *  ``needs_switch`` SENZA creare spedizioni. Il bulk lo usa per decidere se
 *  mostrare il dialog cumulativo di switch SDA→Poste Delivery prima di generare.
 *  Backend: POST /api/orders/labels/preflight → ``{items: [...]}``.
 *
 *  Difensivo (backend sviluppato in parallelo): accetta sia il wrapper
 *  ``{items}`` sia un array nudo; qualsiasi altra forma → lista vuota, così il
 *  chiamante non crasha e — in caso di preflight inaffidabile — il backend
 *  resta comunque la guardia autoritativa in fase di generazione. */
export async function preflightLabels(refs: string[]): Promise<PreflightItem[]> {
  const res = await apiPost<PreflightResponse | PreflightItem[]>("/api/orders/labels/preflight", {
    order_refs: refs,
  });
  if (Array.isArray(res)) return res;
  return res?.items ?? [];
}

export type GenerateLabelBulkItem = {
  order_id: number;
  reference: string;
  file_name: string;
  tracking_code: string;
  carrier: string;
  cache_hit: boolean;
};

export type GenerateLabelBulkError = { reference: string; message: string };

/** Ordine saltato dal bulk perché SDA>15 colli senza consenso allo switch
 *  (allow_carrier_switch=false). Non è un errore: va elencato nel riepilogo. */
export type GenerateLabelBulkSkipped = {
  reference: string;
  packages_count: number;
  proposed_carrier?: string;
};

export type GenerateLabelBulkResult = {
  requested: number;
  generated: GenerateLabelBulkItem[];
  errors: GenerateLabelBulkError[];
  /** Ordini saltati per switch corriere non confermato (campo opzionale:
   *  presente solo quando il backend ha ordini SDA>15 senza consenso). */
  needs_switch?: GenerateLabelBulkSkipped[];
};

export type GenerateLabelsBulkOptions = {
  /** Consenso allo switch SDA→Poste Delivery per gli ordini con >15 colli. */
  allow_carrier_switch?: boolean;
};

/** Genera la LDV per N ordini NON-AllService selezionati (bulk).
 *  Backend: POST /api/orders/labels/bulk → per ogni ordine lo stesso
 *  flusso del singolo generate-label (un PoleepoClient riusato). Esito
 *  per-ordine: ``generated`` + ``errors`` (mai un'azione muta).
 *
 *  ``allow_carrier_switch``: con true, gli ordini SDA con >15 colli vengono
 *  generati con Poste Delivery; con false (default) finiscono in
 *  ``needs_switch`` (saltati). ``undefined`` viene omesso da ``JSON.stringify``,
 *  così il body resta ``{order_refs}`` quando il flag non serve. */
export function generateLabelsBulk(
  orderRefs: string[],
  options: GenerateLabelsBulkOptions = {},
): Promise<GenerateLabelBulkResult> {
  return apiPost<GenerateLabelBulkResult>("/api/orders/labels/bulk", {
    order_refs: orderRefs,
    allow_carrier_switch: options.allow_carrier_switch,
  });
}

export type ShippingPackageInput = { kg: number; l: number; a: number; p: number };

export type UpdateShippingResult = {
  order_id: number;
  reference: string;
  packages_count: number;
  use_pallet: boolean;
  package_dimensions: ShippingPackageInput[];
};

/** Salva "al volo" i dati spedizione di un ordine (colli/peso/dimensioni).
 *  Backend: PATCH /api/orders/{ref}/shipping → campi LOCAL_ONLY che
 *  sopravvivono al sync Poleepo e hanno priorita' nella generazione LDV.
 *  Per la LDV conta soprattutto il peso: le dimensioni possono restare 0. */
export function updateOrderShipping(
  orderRef: string,
  body: { packages_count: number; use_pallet: boolean; package_dimensions: ShippingPackageInput[] },
): Promise<UpdateShippingResult> {
  return apiPatch<UpdateShippingResult>(
    `/api/orders/${encodeURIComponent(orderRef)}/shipping`,
    body,
  );
}

export type SetSupplierOrderRefResult = {
  order_id: number;
  reference: string;
  supplier_order_ref: string;
};

/** Setta a mano il N.Ord.Forn dell'ordine (numero sost / Cosma).
 *  Backend: PATCH /api/orders/{ref}/supplier-order-ref. Campo LOCAL_ONLY
 *  (sopravvive al sync) e lo scraper salta i non-vuoti -> non sovrascrive. */
export function setSupplierOrderRef(
  orderRef: string,
  supplierOrderRef: string,
): Promise<SetSupplierOrderRefResult> {
  return apiPatch<SetSupplierOrderRefResult>(
    `/api/orders/${encodeURIComponent(orderRef)}/supplier-order-ref`,
    { supplier_order_ref: supplierOrderRef },
  );
}

/** Risposta di PUT e DELETE del terzo indirizzo: lo stato dopo la scrittura.
 *  Snake_case, è un DTO di passaggio (stesso schema di `SetSupplierOrderRefResult`). */
export type ReplacementAddressResult = {
  order_id: number;
  reference: string;
  replacement_address: ReplacementAddress | null;
  replacement_note: string | null;
  replacement_set_at: string | null;
};

/** Registra (o corregge) l'indirizzo di SOSTITUZIONE dell'ordine.
 *  Backend: PUT /api/orders/{ref}/replacement-address. Solo `address` (la via)
 *  è obbligatorio — 422 se vuoto: senza via la merce non parte. Il resto
 *  (CAP, telefono) può mancare, la cliente è al telefono e si completa dopo.
 *
 *  ⚠️ `replacement_set_at` torna la data della PRIMA registrazione anche
 *  quando questa chiamata è una correzione: è voluto, è l'orologio del
 *  promemoria e non deve ripartire a ogni CAP sistemato. */
export function salvaIndirizzoSostituzione(
  orderRef: string,
  address: ReplacementAddress,
  note = "",
): Promise<ReplacementAddressResult> {
  return apiPut<ReplacementAddressResult>(
    `/api/orders/${encodeURIComponent(orderRef)}/replacement-address`,
    { address, note },
  );
}

/** Toglie la sostituzione dall'ordine: indirizzo, nota e data insieme.
 *  Backend: DELETE /api/orders/{ref}/replacement-address, idempotente.
 *  Non ritorna nulla di utile — dopo la cancellazione i tre campi sono `null`
 *  per definizione: chi chiama ricarica l'ordine (o li azzera in locale). */
export function togliIndirizzoSostituzione(orderRef: string): Promise<void> {
  return apiDelete(`/api/orders/${encodeURIComponent(orderRef)}/replacement-address`);
}

export type SendPickupMailResult = {
  order_id: number;
  reference: string;
  supplier_ref: string;
  supplier_kind: FornitoreKind;
  recipient: string;
  subject: string;
  attachment_name: string;
  message_id: string;
  /** Audit 2026-05-13: backend ritorna ``ldv_auto_generated=True`` se
   *  ha dovuto rigenerare/recuperare la LDV automaticamente (fix #3
   *  audit 2026-05-12). Frontend puo' mostrare feedback "LDV creata
   *  automaticamente prima di aprire Thunderbird". */
  ldv_auto_generated?: boolean;
  /** Bug 2026-06-25 (v1.2.12): ordine SDA con >15 colli → il pickup mail
   *  auto-switcha a Poste Delivery Business (un click = consenso). Valorizzato
   *  col corriere ("POSTE_DELIVERY") solo se lo switch è avvenuto, così il
   *  frontend mostra l'avviso del cambio corriere. ``null``/assente = nessuno
   *  switch. */
  carrier_switched_to?: string | null;
  /** Numero colli che ha fatto scattare lo switch (solo per il messaggio
   *  "passato a Poste Delivery, N colli"). */
  packages_count?: number | null;
};

/** Apre Thunderbird con mail di spedizione pre-compilata per un ordine
 *  Itamoby/Ityhome "pronto al ritiro". Davide clicca "Invia" manualmente
 *  in TB (account email gia' configurato, no SMTP). Replica del bottone
 *  legacy "Invia email di spedizione" (purple). Allega la LDV PDF
 *  generata in precedenza con generateLabel().
 *  ``opts`` assente/senza ``preview`` → body `{}` e ritorno invariati (chiamata
 *  desktop esistente). ``preview: true`` → union esplicita (bozza senza invio,
 *  va discriminata con ``"preview" in result``); ``mailOverride`` sovrascrive
 *  destinatario/oggetto/corpo prima dell'invio. */
export function sendPickupMail(
  orderRef: string,
  opts?: { preview?: false; mailOverride?: MailOverride },
): Promise<SendPickupMailResult>;
export function sendPickupMail(
  orderRef: string,
  opts: { preview: boolean; mailOverride?: MailOverride },
): Promise<SendPickupMailResult | MailPreviewRead>;
export function sendPickupMail(
  orderRef: string,
  opts?: { preview?: boolean; mailOverride?: MailOverride },
): Promise<SendPickupMailResult | MailPreviewRead> {
  return apiPost<SendPickupMailResult | MailPreviewRead>(
    `/api/orders/${encodeURIComponent(orderRef)}/send-pickup-mail`,
    opts ? { preview: opts.preview ?? false, mail_override: opts.mailOverride ?? null } : {},
  );
}

/** Fetch riepilogo "Ritiri di oggi" aggregato per carrier (SDA, BRT
 *  con split colli/bancali, OTHER). Polling auto ogni 60s — il dato
 *  cambia quando arriva una nuova mail "merce pronta" o quando Davide
 *  genera una LDV (che popola package_dimensions). */
export function useRitiriOggi(enabled: boolean): RitiriOggi | null {
  const [data, setData] = useState<RitiriOggi | null>(null);
  // Audit 2026-05-13: rimosso ``mountedRef`` ridondante. ``AbortController.abort``
  // nel cleanup cancella la fetch in volo, e ``catch {}`` silenzia l'AbortError.
  // Se ``apiGet`` ignorasse il signal (mock), il setState su componente
  // smontato e' gestito da React 18 StrictMode senza warning utili — il
  // mountedRef era false-safety.
  useEffect(() => {
    if (!enabled) return undefined;
    const ctrl = new AbortController();
    const fetchOnce = async () => {
      try {
        const result = await apiGet<RitiriOggi>("/api/orders/pickup-summary", {
          signal: ctrl.signal,
        });
        setData(result);
      } catch {
        // Errori di rete sono soft: la card mostra l'ultimo dato noto.
      }
    };
    // ``setTimeout`` a catena: il giro dopo parte DALLA risposta, non
    // dall'orologio. Misurato in produzione il 2026-09-08: pickup-summary
    // rispondeva in media 91 s con il timer a 60 — ogni minuto ne partiva una
    // in piu' di quante ne rientravano, e ognuna trattiene una connessione del
    // pool mentre aspetta il PIM via postgres_fdw (viste 19-32 s di attesa su
    // ``PostgresFdwGetResult``, transazione aperta).
    let timerId: number | null = null;
    const giro = async (): Promise<void> => {
      await fetchOnce();
      if (!ctrl.signal.aborted) timerId = window.setTimeout(() => void giro(), 60_000);
    };
    void giro();
    return () => {
      ctrl.abort();
      if (timerId !== null) window.clearTimeout(timerId);
    };
  }, [enabled]);
  return data;
}

type UseOrdersArgs = {
  enabled: boolean;
  search: string;
  dateFrom: string;
  dateTo: string;
};

export function useOrders({ enabled, search, dateFrom, dateTo }: UseOrdersArgs) {
  const [orders, setOrders] = useState<Order[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Audit 2026-05-04 #12: separare il messaggio di successo dallo slot
  // ``error`` evita la visualizzazione confusa "sync avviato" come
  // warning giallo. Il consumer (OrdiniPage) usa lo stile success.
  const [info, setInfo] = useState<string | null>(null);
  const requestSeq = useRef(0);
  // Ref per cancellare il setTimeout di refresh post-sync se il componente
  // smonta nei 2.5s (audit HIGH 2026-04-29: setTimeout orfani facevano
  // setState su hook smontato).
  const refreshTimerRef = useRef<number | null>(null);
  // Audit 2026-05-11 H8: ``mountedRef`` per evitare setState post-unmount.
  // ``requestSeq`` da solo non protegge il ``finally`` perche' la
  // ``setIsLoading(false)`` viene chiamata anche quando ``seq === requestSeq.current``
  // su un hook gia' smontato (es. route change durante una fetch lenta).
  // React 18 logga warning in questo caso; il guard mountedRef silenzia
  // e previene memory leak di update queue su nodi smontati.
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const refresh = useCallback(
    async (signal?: AbortSignal) => {
      if (!enabled) return;
      const seq = ++requestSeq.current;
      setIsLoading(true);
      try {
        const params = new URLSearchParams({ limit: "500" });
        if (search.trim()) params.set("q", search.trim());
        // Già ISO yyyy-mm-dd: i filtri sono <input type="date">. Vuoto = nessun
        // limite → il parametro si omette e il backend usa il suo default.
        if (dateFrom) params.set("date_from", dateFrom);
        if (dateTo) params.set("date_to", dateTo);
        const response = await apiGet<ApiOrderList>(`/api/orders?${params.toString()}`, {
          signal,
        });
        if (!mountedRef.current) return;
        if (seq === requestSeq.current) {
          setOrders(response.items.map(mapOrder));
          setTotal(response.total);
          setError(null);
        }
      } catch (err) {
        // Stessa guardia del ramo di successo e del ``finally``: ``refresh`` è
        // chiamata anche SENZA signal (timer post-sync, drain-complete, azioni
        // bulk), e lì ``signal?.aborted`` non scatta mai — il fallimento di una
        // richiesta già superata accendeva il banner d'errore sopra una lista
        // appena caricata bene. Stesso fix già presente in catalogo/api.ts.
        if (signal?.aborted || seq !== requestSeq.current) return;
        if (!mountedRef.current) return;
        setError(prettifyApiError(err));
      } finally {
        if (mountedRef.current && seq === requestSeq.current) setIsLoading(false);
      }
    },
    [enabled, search, dateFrom, dateTo],
  );

  const syncNow = useCallback(
    async ({ dateFrom, dateTo }: { dateFrom: string; dateTo: string }) => {
      setIsSyncing(true);
      try {
        await apiPost("/api/orders/sync", {
          date_from: dateFrom || null,
          date_to: dateTo || null,
        });
        setInfo("Sync ordini avviato. Aggiorno la lista tra pochi secondi.");
        setError(null);
        if (refreshTimerRef.current !== null) {
          window.clearTimeout(refreshTimerRef.current);
        }
        refreshTimerRef.current = window.setTimeout(() => {
          refreshTimerRef.current = null;
          setInfo(null);
          void refresh();
        }, 2500);
      } catch (err) {
        // Audit 2026-05-11 C4: in passato qui facevamo ``setError(message)``
        // E rilanciavamo l'errore. Il caller (OrdiniPage) gia' cattura il
        // rilancio e setta ``actionError``, quindi mostravamo DUE pill
        // identiche "Failed to fetch" affiancate (una da ``liveOrders.error``,
        // una da ``actionError``). Lasciamo che il caller gestisca la
        // visualizzazione tramite il rilancio; ``setInfo(null)`` ripulisce
        // il banner ottimistico "Sync avviato".
        setInfo(null);
        throw err instanceof Error ? err : new Error("Errore sconosciuto");
      } finally {
        setIsSyncing(false);
      }
    },
    [refresh],
  );

  useEffect(() => {
    if (!enabled) return;
    const controller = new AbortController();
    void refresh(controller.signal);
    return () => controller.abort();
  }, [enabled, refresh]);

  // Cleanup unmount: cancella eventuale timer pendente per evitare setState
  // su hook smontato.
  useEffect(() => {
    return () => {
      if (refreshTimerRef.current !== null) {
        window.clearTimeout(refreshTimerRef.current);
        refreshTimerRef.current = null;
      }
    };
  }, []);

  return { orders, total, isLoading, isSyncing, error, info, refresh, syncNow };
}

export function mapOrder(order: ApiOrder): Order {
  const firstItem = order.items[0];
  const skus = order.items.map((item) => item.sku).filter(Boolean);
  // Righe ordine con qta: e' il dato che il backend invia tale e quale
  // nel CSV Cosma (colonna ``qta``) / xlsx AllService. Lo esponiamo in UI
  // perche' la card prima nascondeva la quantita' (rischio: ordine da 2
  // pezzi percepito come 1). ``quantity`` fallback 1 = stesso default del
  // mapper backend (mapper.py ``int(row.get("quantity") or 1)``).
  const orderItems = order.items.map((item) => ({
    // id propagato per il proxy foto per-riga /api/orders/items/{id}/thumb
    // (item 16 — ProductsSection). Assente nei mock → null → placeholder.
    id: item.id ?? null,
    sku: item.sku,
    title: item.title,
    quantity: Math.max(1, Math.trunc(Number(item.quantity) || 1)),
    unitPrice: item.unit_price != null ? Number(item.unit_price) : undefined,
  }));
  const totalQuantity = orderItems.reduce((sum, item) => sum + item.quantity, 0);
  const supplierName = supplierBadgeName({
    supplierOrderRef: order.supplier_order_ref || "",
    kind: order.supplier_kind ?? "",
    cosmaSentAt: order.cosma_sent_at ?? "",
    skus,
  });
  const total = Number(order.total_amount || 0);
  // Costruisce l'URL del proxy backend `/api/orders/items/{id}/thumb` invece
  // di puntare alla CDN remota direttamente: la CSP Electron blocca i CDN
  // HTTPS esterni (`img-src` autorizza solo localhost). Il backend scarica
  // una volta e cache su disco. Si attiva solo se l'item ha una immagine
  // remota nota (image_url popolato), altrimenti il frontend mostra il
  // placeholder.
  // 2026-07-03: il thumb va SEMPRE tentato, anche senza image_url nel raw —
  // il backend puo' avere il blob persistito nel DB o recuperare la foto dal
  // prodotto Poleepo (fallback). Su 404 la <Thumbnail> degrada a placeholder
  // via onError; la negative-cache lato server rende il 404 economico.
  const itemWithImage =
    order.items.find((item) => item.image_url && item.id) ?? order.items.find((item) => item.id);
  // Audit 2026-05-19 (Davide "Mancano le foto"): URL ASSOLUTO al backend
  // tramite ``apiAssetUrl``. In dev (vite) il proxy ``/api`` ogni tanto
  // non serviva correttamente le ``<img src>`` lazy-loaded, mostrando il
  // placeholder anche con backend OK; con assoluto il browser carica
  // direttamente da ``http://127.0.0.1:8766`` (CSP esplicitamente
  // autorizzato come img-src).
  const thumbnail = itemWithImage
    ? apiAssetUrl(`/api/orders/items/${itemWithImage.id}/thumb`)
    : null;
  // supplier_cost === null nel response API → "non noto" nell'UI.
  // Discriminiamo da supplier_cost === 0 ("zero reale") per l'auditability
  // del margine.
  const costo: number | null =
    order.supplier_cost === null ? null : Number(order.supplier_cost || 0);

  return {
    id: order.reference || `POL-${order.poleepo_id}`,
    productName: firstItem?.title || skus.join(", ") || `Ordine ${order.reference}`,
    sku: skus.join(", ") || "--",
    items: orderItems,
    totalQuantity,
    thumbnail,
    customer: {
      name: order.customer_name || "Cliente non disponibile",
      address: order.shipping_address,
      city: [order.shipping_postcode, order.shipping_city, order.shipping_province]
        .filter(Boolean)
        .join(" "),
      email: order.customer_email,
      phone: order.customer_phone,
      postcode: order.shipping_postcode || undefined,
      province: order.shipping_province || undefined,
      country: order.shipping_country || undefined,
      cityName: order.shipping_city || undefined,
      addressEditedAt: order.shipping_address_edited_at ?? null,
    },
    total,
    marketplace: mapMarketplace(order.channel),
    // Bucket e codice tradotto arrivano gia' dal backend (mapper.normalize_status).
    state: order.state,
    status: order.status,
    paymentState: order.payment_state,
    fornitore: {
      name: supplierName,
      // supplier_kind dal backend e' fonte primaria (deriva da raw fulfillments
      // Poleepo). Fallback sull'inferenza locale via SKU per ordini storici.
      kind: order.supplier_kind ?? inferSupplierKind(skus, supplierName),
    },
    // Pass-through del flag pickup_ready calcolato server-side: il UI
    // mostra il badge "PRONTO AL RITIRO" senza dover ricalcolare la
    // logica (state ∉ spedito/annullato + supplier ∈ ITY/ITAM + raw.merce_pronta).
    // Fase A multi-fornitore: kind per singolo item + flag "misto".
    itemSupplierKinds: order.item_supplier_kinds ?? null,
    fornitoriMerce: order.fornitori_merce ?? null,
    isMixed: order.is_mixed === true,
    pickupReady: order.pickup_ready === true,
    // Idem per il flag "LDV gia' inviata": lo scanner IMAP Sent ha
    // riconosciuto una mail al fornitore con LDV allegata.
    pickupMailSent: order.pickup_mail_sent === true,
    pickupMailSentAt: order.pickup_mail_sent_at ?? null,
    merceProntaToday: order.merce_pronta_today === true,
    pickupMailMissing: order.pickup_mail_missing === true,
    createdAtPoleepo: order.created_at_poleepo ?? null,
    updatedAtPoleepo: order.updated_at_poleepo ?? null,
    corriere: order.carrier || "SDA",
    // Audit 2026-05-14: se packages_count e' 0 (default Poleepo per ordini
    // freschi) ma il backend ha matchato lo SKU nel listino fornitore,
    // mostriamo il count dei colli suggeriti come default editable. Questo
    // permette la generazione LDV automatica senza digitazione manuale.
    colli:
      order.packages_count > 0
        ? order.packages_count
        : (order.suggested_packages_count ?? order.suggested_packages?.length ?? 0),
    suggestedPackages: order.suggested_packages ?? null,
    isDuplicate: order.is_duplicate === true,
    usePallet: order.use_pallet === true,
    usaBancale: order.usa_bancale === true,
    packageDimensions: order.package_dimensions ?? [],
    // Order.costo resta `number` per compatibilita' con mockOrders e UI;
    // null mappato a 0 nel campo serializzato, ma il margine usa la
    // distinzione null vs 0 via il reasoning in mapOrder.
    costo: costo ?? 0,
    costoNoto: costo !== null,
    portaleFornitore: order.portale_fornitore ?? null,
    margin: formatMargin(total, costo),
    // Pass-through del raw del backend: contiene as_mail_app_sent_at,
    // as_mail_imap_confirmed_at, as_mail_app_attempts ecc. usati dal
    // MailStatusBadge senza ulteriori round-trip API.
    raw: order.raw ?? null,
    supplierOrderRef: order.supplier_order_ref || undefined,
    inWarehouse: order.in_warehouse === true,
    allserviceEligible: order.allservice_eligible ?? undefined,
    wfEligible: order.wf_eligible === true,
    fornitoreMail: order.fornitore_mail ?? null,
    hasMail: order.has_outbox_mail === true,
    trackingCode: order.tracking_code ?? null,
    lastTrackingEvent: mapTrackingEvent(order.last_tracking_event),
    shipDeadline: order.ship_deadline ?? null,
    supplierUnpaid: order.supplier_unpaid === true,
    paid: order.paid === true,
    supplierSendState: order.supplier_send_state ?? null,
    customerUnpaid: order.customer_unpaid === true,
    hasReturn: order.has_return === true,
    hasRefund: order.has_refund === true,
    addressIssues: order.address_issues ?? [],
    righeRidotte: order.righe_ridotte ?? [],
    invoice: order.invoice ?? null,
    // ⚠️ `?? null`, MAI `|| null`: un rimborso di 0,00 è un caso reale (storno
    // già azzerato) e `||` lo tratterebbe come «importo ignoto», facendo dire
    // al badge «da confermare» su un importo che invece è confermatissimo.
    refundAmount: order.refund_amount != null ? Number(order.refund_amount) : null,
    currency: order.currency ?? undefined,
    ldvCode: order.ldv_code || undefined,
    replacementAddress: order.replacement_address ?? null,
    replacementNote: order.replacement_note ?? null,
    replacementSetAt: order.replacement_set_at ?? null,
    replacementPending: order.replacement_pending === true,
    replacementTracking: order.replacement_tracking ?? [],
  };
}

/** Hook per la pagina ordine. Stesso pattern di useRitiriOggi:
 *  AbortController nel cleanup, errore prettificato in italiano. */
export function useOrderDetail(orderRef: string | undefined): {
  order: Order | null;
  isLoading: boolean;
  error: string | null;
  reload: () => void;
} {
  const [order, setOrder] = useState<Order | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);
  const reload = useCallback(() => setNonce((n) => n + 1), []);

  useEffect(() => {
    if (!orderRef) {
      setOrder(null);
      setIsLoading(false);
      setError(null);
      return undefined;
    }
    const ctrl = new AbortController();
    setIsLoading(true);
    setError(null);
    const run = async () => {
      try {
        const api = await apiGet<ApiOrder>(`/api/orders/${encodeURIComponent(orderRef)}`, {
          signal: ctrl.signal,
        });
        // Stessa guardia di catch/finally: una risposta risolta appena prima
        // dell'abort scriveva l'ordine VECCHIO sotto la URL del nuovo.
        if (ctrl.signal.aborted) return;
        setOrder(mapOrder(api));
      } catch (err) {
        if (ctrl.signal.aborted) return;
        setError(prettifyApiError(err));
        setOrder(null);
      } finally {
        if (!ctrl.signal.aborted) setIsLoading(false);
      }
    };
    void run();
    return () => ctrl.abort();
  }, [orderRef, nonce]);

  return { order, isLoading, error, reload };
}

/** Le spedizioni REALI dell'ordine, per la sezione Spedizioni della pagina.
 *
 *  Gate a 2+ spedizioni: ``Order.tracking_code``/``carrier``/``ldvCode`` sono
 *  scalari singoli e ShippingSection li mostra gia'. Solo 29 ordini su 4561 ne
 *  hanno di piu' — per gli altri 4532 questa sezione non aggiungerebbe nulla e
 *  la GET costerebbe una chiamata Poleepo per spedizione. Il gate si legge in
 *  locale: ``raw["shippings"]`` e' gia' nel browser (``OrderRead.raw`` non ha
 *  exclude), quindi non paga un round-trip per scoprire di non servire.
 *
 *  Errore → lista vuota, MAI un errore in pagina. Vale per Poleepo KO (502:
 *  la sezione sparisce, il dossier resta) e per i test di OrderDetailPage, dove
 *  ``apiGet`` e' mockato UNA volta per tutte le GET e risponde l'ordine finto a
 *  qualunque path: senza il ``catch`` un parse fallito qui farebbe cadere gli
 *  11 test della pagina. */
export function useOrderShipments(order: Order | null | undefined): OrderShipment[] {
  const [shipments, setShipments] = useState<OrderShipment[]>([]);
  const orderRef = order?.id;
  const shippings = order?.raw?.shippings;
  const isMultiShipment = Array.isArray(shippings) && shippings.length >= 2;

  useEffect(() => {
    if (!orderRef || !isMultiShipment) {
      setShipments([]);
      return undefined;
    }
    const ctrl = new AbortController();
    let cancelled = false;
    apiGet<{ shipments: OrderShipment[] }>(
      `/api/orders/${encodeURIComponent(orderRef)}/shipments`,
      { signal: ctrl.signal },
    )
      .then((res) => {
        // Array.isArray e non ``?? []``: il mock dei test risponde con l'ordine
        // finto, dove ``shipments`` non e' una lista.
        if (!cancelled) setShipments(Array.isArray(res.shipments) ? res.shipments : []);
      })
      .catch(() => {
        if (!cancelled) setShipments([]);
      });
    return () => {
      cancelled = true;
      ctrl.abort();
    };
  }, [orderRef, isMultiShipment]);

  return shipments;
}

function mapMarketplace(channel: string): Marketplace {
  const normalized = channel.toLowerCase();
  if (normalized.includes("amazon")) return "Amazon";
  if (normalized.includes("ebay")) return "eBay";
  if (normalized.includes("shopify")) return "Shopify";
  return "Privato";
}

function inferSupplierName(skus: string[]) {
  // Audit 2026-05-11: prima ``sku.startsWith("FE") || startsWith("MO")``
  // matchava qualsiasi SKU con quel prefisso (es. ``FE802584-TORT`` =
  // sedia LEVI Itamoby) facendo apparire ordini non-AllService come
  // "ALL SERVICE". Ora usiamo la whitelist esatta ALLSERVICE_SKUS.
  if (skusContainAllService(skus)) return "ALL SERVICE";
  return "Da sincronizzare";
}

/** Data DD/MM/YYYY da un ISO ("2026-06-12T..." → "12/06/2026"); "" se non parsabile. */
function formatCosmaSentDate(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : "";
}

/**
 * Etichetta del badge fornitore (N.Ord.Forn).
 * - numero fornitore sincronizzato (Itamoby/Ityhome via WooCommerce) → vince;
 * - Cosma (FTP one-way, nessun numero torna in OM) → stato invio
 *   "Inviato a Cosma" / "Da inviare a Cosma" invece del fuorviante
 *   "Da sincronizzare" (bug 2026-06-15);
 * - altri → euristica SKU (``inferSupplierName``).
 */
export function supplierBadgeName(opts: {
  supplierOrderRef: string;
  kind: string;
  cosmaSentAt: string;
  skus: string[];
}): string {
  if (opts.supplierOrderRef) return opts.supplierOrderRef;
  if (opts.kind === "cosma") {
    if (!opts.cosmaSentAt) return "Da inviare a Cosma";
    const date = formatCosmaSentDate(opts.cosmaSentAt);
    return date ? `Inviato a Cosma ${date}` : "Inviato a Cosma";
  }
  return inferSupplierName(opts.skus);
}

function inferSupplierKind(skus: string[], supplierName: string): FornitoreKind {
  const normalized = supplierName.toLowerCase();
  if (normalized.includes("ity")) return "ityhome";
  if (normalized.includes("itam")) return "itamoby";
  // Audit 2026-05-11 H7: prima ``normalized.includes("all")`` matchava
  // QUALSIASI nome con sottostringa "all" (es. "GALLI-2026", "Hallmark",
  // "Atallah" → tutti marcati allservice). Richiedi ENTRAMBE "all" e
  // "service" come fa il backend ``_supplier_kind_from_name``
  // (schemas.py:22). Mantiene match per "ALL SERVICE", "All Service Srl"
  // ecc. senza false positive sui nomi che hanno solo "all".
  if (
    (normalized.includes("all") && normalized.includes("service")) ||
    skusContainAllService(skus)
  ) {
    return "allservice";
  }
  // Audit HIGH-5 2026-04-29: prima ritornavamo "itamoby" come fallback,
  // facendo apparire ordini di fornitori sconosciuti sotto un brand
  // sbagliato. Ora marchiamo "unknown" — l'UI puo' filtrarli con il
  // chip dedicato (futuro) o lasciarli fuori dal default Itamoby/Ityhome.
  return "unknown";
}

export function formatMargin(total: number, costo: number | null) {
  // costo === null = "non noto" (mai sincronizzato dal fornitore);
  // costo === 0  = "zero reale" (es. campioni gratuiti). Distingue il caso
  // ambiguo del !costo legacy: prima ritornavamo "--" sia per costo
  // unknown sia per costo zero (audit LOW 2026-04-29).
  if (!total) return "--";
  if (costo === null) return "—"; // em-dash: costo non disponibile
  // Fix margine (audit 2026-06-24): ``total`` e' il prezzo cliente LORDO
  // (IVA inclusa) mentre ``costo`` (supplier_cost) e' l'imponibile SENZA
  // IVA. La vecchia formula ``(total - costo) / total`` mescolava le due
  // basi → margine gonfiato (~+14 punti) e divergente dal dialog
  // "Conteggio €" (economicSummary), che e' IVA-corretto. Portiamo i due
  // termini sulla STESSA base netta: scorporiamo l'IVA dal totale cliente
  // (``total / (1 + IVA_RATE)``) e lo confrontiamo col costo gia' netto.
  // ``IVA_RATE`` e' la costante condivisa di economicSummary (no duplicati).
  //
  // ASSUNZIONE (Task 10, audit 2026-06-24): aliquota IVA = 22% (standard IT
  // arredamento, ``IVA_RATE`` = 0.22). Il margine vale finché tutto il carrello
  // è al 22%; per ordini con aliquota mista (es. beni agevolati) sarebbe
  // approssimato. Caso fuori scope oggi (Davide vende arredamento full-22%): se
  // emergesse, andrebbe passata l'aliquota reale per riga ordine dal backend.
  // L'etichetta UI (tooltip "IVA 22% scorporata") è omessa per restare nello
  // scope di api.ts — vedi nota del piano; eventuale follow-up cosmetico.
  const netTotal = total / (1 + IVA_RATE);
  return `+${(((netTotal - costo) / netTotal) * 100).toLocaleString("it-IT", {
    maximumFractionDigits: 1,
  })}%`;
}

// ── Bulk change-status (#11 legacy parity) ────────────────────────────────

export interface BulkChangeStatusError {
  reference: string;
  error: string;
}

export interface BulkChangeStatusResult {
  updated: number;
  errors: BulkChangeStatusError[];
}

/** Status Poleepo selezionabili dal dropdown bulk (replica del legacy
 *  ``ORDER_STATUSES`` in ``Poleepo Manager App/core/config.py:36-46``).
 *  Subset operativo: i piu' usati da Davide nei workflow quotidiani. */
export const POLEEPO_BULK_STATUSES: ReadonlyArray<{ value: string; label: string }> = [
  { value: "PAYMENT_ACCEPTED", label: "Pagato" },
  { value: "WORK_IN_PROGRESS", label: "In lavorazione" },
  { value: "READY_TO_SHIP", label: "Pronto al ritiro" },
  { value: "PICKUP_IN_STORE", label: "Ritirato in negozio" },
  { value: "FULL_SHIPPED", label: "Spedito" },
  { value: "COMPLETED", label: "Completato" },
  { value: "CANCELLED_BY_SELLER", label: "Annullato (venditore)" },
  { value: "CANCELLED_BY_CUSTOMER", label: "Annullato (cliente)" },
  { value: "REFUND", label: "Rimborso" },
];

export function bulkChangeStatus(
  orderRefs: string[],
  newStatus: string,
): Promise<BulkChangeStatusResult> {
  return apiPost<BulkChangeStatusResult>("/api/orders/bulk-change-status", {
    order_refs: orderRefs,
    new_status: newStatus,
  });
}

/** Colli dell'ordine per il popup tracking della card (design B 2026-07-02):
 *  estrazione lazy dal PDF LDV lato backend, cache su parcel_tracking_codes. */
export async function fetchOrderParcelCodes(orderRef: string): Promise<string[]> {
  const res = await apiGet<{ parcels: Array<{ code: string }> }>(
    `/api/orders/${encodeURIComponent(orderRef)}/parcels`,
  );
  return res.parcels.map((p) => p.code).filter(Boolean);
}

/** Customer-360 (Piano 6): risposta di GET /orders/{ref}/customer già mappata. */
export interface CustomerOrders {
  customer: { email: string; name: string; city: string; isFallbackKey: boolean };
  /** Ricavo lordo (somma total_amount dedup, stessa currency) — MAI margine. */
  ltv: number;
  currency: string;
  ordersCount: number;
  orders: Order[];
}

interface ApiCustomerOrders {
  customer: { email: string; name: string; city: string; is_fallback_key: boolean };
  ltv: string;
  currency: string;
  orders_count: number;
  first_order_at: string | null;
  last_order_at: string | null;
  orders: ApiOrder[];
}

export async function fetchCustomerOrders(
  orderRef: string,
  signal?: AbortSignal,
): Promise<CustomerOrders> {
  const res = await apiGet<ApiCustomerOrders>(
    `/api/orders/${encodeURIComponent(orderRef)}/customer`,
    signal ? { signal } : undefined,
  );
  return {
    customer: {
      email: res.customer.email,
      name: res.customer.name,
      city: res.customer.city,
      isFallbackKey: res.customer.is_fallback_key,
    },
    ltv: Number(res.ltv),
    currency: res.currency,
    ordersCount: res.orders_count,
    orders: res.orders.map(mapOrder),
  };
}

// ── Chiamate cliente (CRM, item 4) ────────────────────────────────────────

/** Motivi ammessi per una chiamata (mirror di ``CallReason`` backend). */
export type CallReason =
  | "info_spedizione"
  | "reclamo"
  | "reso_sostituzione"
  | "info_prodotto"
  | "sollecito_pagamento"
  | "altro";

/** Chiamata cliente registrata (GET /api/crm/calls), campi camelCase. */
export interface OrderCall {
  id: number;
  createdAt: string;
  reason: CallReason;
  note: string | null;
}

interface ApiCall {
  id: number;
  created_at: string;
  order_reference: string | null;
  customer_email: string | null;
  customer_name: string | null;
  reason: CallReason;
  note: string | null;
}

/** Chiamate registrate per un ordine (piu' recenti prima). */
export async function fetchOrderCalls(
  orderRef: string,
  signal?: AbortSignal,
): Promise<OrderCall[]> {
  const rows = await apiGet<ApiCall[]>(
    `/api/crm/calls?order_ref=${encodeURIComponent(orderRef)}`,
    signal ? { signal } : undefined,
  );
  return rows.map((r) => ({
    id: r.id,
    createdAt: r.created_at,
    reason: r.reason,
    note: r.note,
  }));
}

// ── Apertura pratica SDA (item 3) ─────────────────────────────────────────

// ``filenameFromContentDisposition`` è stato spostato in ``@/shared/api`` (concern
// HTTP generico, riusato anche da fatturazione). Re-export per compatibilità con
// i consumer/test che lo importano ancora da qui.
export { filenameFromContentDisposition };

/** Voci delle tendine del modulo SDA (causa reclamo, tipo imballo, imballo
 *  interno). Vengono dal TEMPLATE lato backend e non da una copia qui: le celle
 *  hanno data-validation a lista, e un valore fuori lista fa aprire il file con
 *  "valore non valido". Ricopiarle in TS le farebbe divergere alla prima
 *  revisione SDA — cioe' romperebbe il file esattamente quando SDA cambia. */
export interface DisputeOptions {
  causa: string[];
  imballo: string[];
  imballo_interno: string[];
}

export function fetchDisputeCausali(): Promise<DisputeOptions> {
  return apiGet<DisputeOptions>("/api/orders/dispute/causali");
}

/** Scarica l'XLSX "APERTURA PRATICA SDA {ref}" (GET /orders/{ref}/dispute-file)
 *  in modo coesistenza-safe: l'anchor con ``download`` apre il Save-As nel
 *  browser web e viene intercettato da ``will-download`` in Electron (salva in
 *  ~/Downloads). Il nome file lo detta il backend (autoritativo) via
 *  Content-Disposition; fallback locale se l'header non e' leggibile. Fetch
 *  esplicita cosi' un errore HTTP diventa un throw leggibile invece del
 *  silenzioso "non succede niente" (stesso motivo di AttachmentList).
 *
 *  ``causa``: Tipo Pratica scelto dall'operatore, passato VERBATIM (niente
 *  trim: una causale del modulo ha uno spazio finale che fa parte del valore). */
/** Scarica il PDF della LDV gia' generata (GET /orders/{ref}/label).
 *
 *  NIENTE ``window.open``: dopo un ``await`` il click non e' piu' un gesto
 *  utente e il popup blocker ferma la scheda **senza lanciare** — l'operatore
 *  leggeva «LDV generata» e non vedeva nessun PDF. E aperta in una scheda
 *  nuda, un 404/401 gli mostrava il JSON grezzo dell'API.
 *  ``downloadFile`` usa un anchor ``download`` (immune al blocker), lancia un
 *  errore in italiano su 404/500 e sul 401 porta alla login. */
export async function downloadOrderLabel(orderRef: string): Promise<void> {
  await downloadFile(`/api/orders/${encodeURIComponent(orderRef)}/label`, `LDV ${orderRef}.pdf`);
}

/** Scarica il borderò BRT (GET /orders/{ref}/bordero). Stessa ragione di
 *  ``downloadOrderLabel``: era una scheda nuda, e su errore mostrava JSON. */
export async function downloadOrderBordero(orderRef: string): Promise<void> {
  await downloadFile(
    `/api/orders/${encodeURIComponent(orderRef)}/bordero`,
    `Bordero ${orderRef}.pdf`,
  );
}

export async function downloadDisputeFile(orderRef: string, causa?: string): Promise<void> {
  const qs = causa ? `?causa=${encodeURIComponent(causa)}` : "";
  // ``downloadFile``: sul 401 porta alla login (in web-mode) e l'errore del
  // backend arriva in italiano. Qui c'era `[dispute-file] ${res.status}`, che
  // ``prettifyApiError`` non riconosce e mostrava letteralmente a schermo.
  await downloadFile(
    `/api/orders/${encodeURIComponent(orderRef)}/dispute-file${qs}`,
    `APERTURA PRATICA SDA ${orderRef}.xlsx`,
  );
}
